import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { mapStationMarker } from '../../components/mapMarker';
import { createLocation } from '../../api/stations';
import { ACTIVE, MOVATIC_PRIMARY_COLOR, RADIUS_UPPER_LIMIT } from '../../constants';
import {
  displayError,
  findAccess,
  getPricingString,
  history,
  truncateName,
} from '../../utils';
//@ts-ignore
import { FeatureGroup, Map } from 'react-leaflet/es';
//@ts-ignore
import { EditControl } from 'react-leaflet-draw/dist/esm';
import { GenericMapTileLayer } from '../../components/mapComponent';
import {
  ICoordinates,
  INewStation,
  IStation,
  StationConfiguration,
} from '../../ts/interface/station.interface';
import useRates from '../../hooks/rates/query/useRates';
import { IRate } from '../../ts/interface/rental.interface';
import { ISystem } from '../../ts/interface/system.interface';
import { IMode } from '../../ts/interface/mode.interface';
import { IStationAddProps } from '../../ts/interface/pages/stations.interfaces';
import { Stack } from '@mui/system';
import {
  MovaticFieldGroupCheck,
  MovaticFieldGroupMultiSelect,
  MovaticFieldGroupOutlinedInput,
  MovaticFieldGroupRadio,
  MovaticFieldGroupSelect,
  MovaticFieldGroupText,
} from '../../components/Redesing/movatic-field-group';
import FormItem from '../../components/Redesing/form-item';
import MenuItem from '@mui/material/MenuItem';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import SaveIcon from '@mui/icons-material/Save';
import PageContainer from '../../components/Redesing/page-container';
import CardView from '../../components/Redesing/card-view';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import useOrganization from '../../hooks/use-organization';
import useCurrentSystemInfo from '../../hooks/system/hook/useCurrentSystemInfo';
import OrganizationSystemSelect from '../../components/system/organization-system-select';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

const SLOW_ZONE_ERROR = 'Please enter a valid speed limit';
const RADIUS_ERROR = `Please enter a valid radius value between 1 and ${RADIUS_UPPER_LIMIT}`;
const { ZONE, RACK, DOCK, STORE, WAREHOUSE } = StationConfiguration;

interface StationState {
  stationName: string;
  nameValidationResult: string | null;
  nameValidationHelp: string;
  lat: number | null;
  latValidationResult: string | null;
  latValidationHelp: string;
  lng: number | null;
  longValidationResult: string | null;
  longValidationHelp: string;
  state: number | null;
  validationResult: string | null;
  help: string;
  capacity: number | null;
  isLoading: boolean;
  module: string | null;
  moduleMac?: string | null;
  moduleResult: string | null;
  moduleValidationHelp: string;
  reservations: boolean;
  maxReservations: number;
  zoneCoordinates: number[][];
  configuration: StationConfiguration;
  zoneType: string;
  slowZoneSpeed: string | null;
  slowZoneSpeedHelp: string;
  radius: number;
  radiusValidationResult: string | null;
  radiusValidationHelp: string;
  preventEndingRentals: boolean;
  preventStartingRentals: boolean;
  stations: IStation[];
  stationsLoaded: boolean;
  systemRates: { label: string; value: string }[] | string[];
  ratesLoaded: boolean;
  selectedRates: string[];
  componentRates: string[];
}

const initialState: StationState = {
  stationName: '',
  nameValidationResult: null,
  nameValidationHelp: '',
  lat: null,
  latValidationResult: null,
  latValidationHelp: '',
  lng: null,
  longValidationResult: null,
  longValidationHelp: '',
  state: null,
  validationResult: null,
  help: '',
  capacity: null,
  isLoading: false,
  module: null,
  moduleMac: undefined,
  moduleResult: null,
  moduleValidationHelp: '',
  reservations: false,
  maxReservations: 0,
  zoneCoordinates: [],
  configuration: RACK,
  zoneType: 'parking',
  slowZoneSpeed: null,
  slowZoneSpeedHelp: '',
  radius: 30,
  radiusValidationResult: null,
  radiusValidationHelp: '',
  preventEndingRentals: false,
  preventStartingRentals: false,
  stations: [],
  stationsLoaded: false,
  systemRates: [],
  ratesLoaded: false,
  selectedRates: [],
  componentRates: [],
};

const StationAdd = ({
  systemLoaded,
  systemAccess,
  movaticAccess,
  system,
  modeInfo,
  modules,
}: IStationAddProps) => {
  const organizationInfo = useOrganization();

  const [state, setState] = useState<StationState>(initialState);
  const mapRef = useRef();
  const [marker, setMarker] = useState(null);

  const [selectedSystem, setSelectedSystem] = useState<ISystem | null>(null);

  const { modeInfo: systemModeInfo } = useCurrentSystemInfo(selectedSystem?.gid || '');

  const currentModeInfo = useMemo(
    () => systemModeInfo || modeInfo,
    [systemModeInfo, modeInfo]
  );

  const { data: ratesData, isLoading: ratesLoading } = useRates(
    {
      enabled: systemLoaded,
    },
    selectedSystem?.old_id || system?.id
  ) as { data: IRate[] | undefined; isLoading: boolean };

  // Handle rates data changes (replaces onSuccess callback removed in React Query v5)
  useEffect(() => {
    if (!ratesData) return;
    setState((prevState) => ({
      ...prevState,
      systemRates: ratesData
        .filter((rate: IRate) => !rate.archived)
        .map((rate: IRate) => {
          const priceString = getPricingString({ ...rate, base: rate.base_rate });
          return {
            label: `${truncateName(rate.name)}` + (priceString && ` (${priceString})`),
            value: rate.gid,
          };
        }),
      ratesLoaded: true,
    }));
  }, [ratesData]);

  const getMapRef = () => {
    if (mapRef.current) {
      //@ts-ignore
      return mapRef.current.leafletElement;
    }
    return null;
  };

  const handleMapClick = (e: { latlng: { lat: number; lng: number } }) => {
    let map = getMapRef();
    const lat = parseFloat(e.latlng.lat.toFixed(10));
    const lng = parseFloat(e.latlng.lng.toFixed(10));
    let latValidationResult: string | null = null;
    let latValidationHelp = '';
    let longValidationResult: string | null = null;
    let longValidationHelp = '';

    if (lat > 90) {
      latValidationResult = 'error';
      latValidationHelp = 'Latitude cannot be greater than 90';
    } else if (lat < -90) {
      latValidationResult = 'error';
      latValidationHelp = 'Latitude cannot be less than -90';
    }

    if (lng > 180) {
      longValidationResult = 'error';
      longValidationHelp = 'Longitude cannot be greater than 180';
    } else if (lng < -180) {
      longValidationResult = 'error';
      longValidationHelp = 'Longitude cannot be less than -180';
    }

    removeMarkers();

    setMarker(
      // @ts-ignore
      window.L.marker([e.latlng.lat, e.latlng.lng], {
        icon: mapStationMarker(currentModeInfo.details.hardware_type),
      }).addTo(map)
    );

    setState((prevState) => ({
      ...prevState,
      lat,
      lng,
      latValidationResult,
      latValidationHelp,
      longValidationResult,
      longValidationHelp,
      zoneCoordinates: [],
    }));
  };

  const onCreatePolygon = (e: {
    layer: { editing: { latlngs: [ICoordinates][][] } };
  }) => {
    // save points to state always, but show error if coords are out of bounds
    const mapCoords = e.layer.editing.latlngs[0][0];
    const coords = mapCoords.map((latlng: ICoordinates) => [
      Number(latlng.lat.toFixed(5)),
      Number(latlng.lng.toFixed(5)),
    ]);
    setState((prevState) => ({
      ...prevState,
      zoneCoordinates: coords,
    }));

    if (outOfBounds(coords)) {
      displayError('One or more of the points in your zone are invalid');
    }
  };

  const onEditPolygon = (e: { layers: { toGeoJSON: Function } }) => {
    if (e.layers.toGeoJSON().features.length === 0) {
      // user clicked edit but made no changes
      return;
    }

    // save points to state always, but show error if coords are out of bounds
    const mapCoords = e.layers.toGeoJSON().features[0].geometry.coordinates[0];
    // geoJSON is backwards (lnglat instead of latlng)
    const coords = mapCoords.map((latlng: number[]) => [
      Number(latlng[1].toFixed(5)),
      Number(latlng[0].toFixed(5)),
    ]);

    // the last point tends to be a duplicate of the first one for some reason
    if (
      coords[0][0] === coords[coords.length - 1][0] &&
      coords[0][1] === coords[coords.length - 1][1]
    ) {
      coords.pop();
    }
    setState((prevState) => ({
      ...prevState,
      zoneCoordinates: coords,
    }));

    if (outOfBounds(coords)) {
      displayError('One or more of the points in your zone are invalid');
    }
  };

  const onDeletePolygon = () => {
    setState((prevState) => ({
      ...prevState,
      zoneCoordinates: [],
    }));
  };

  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setState((prevState) => ({
      ...prevState,
      [e.target.name]: value,
    }));
  };

  const handleSlowZoneOptionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prevState) => ({
      ...prevState,
      slowZoneSpeed: e.target.value,
      slowZoneSpeedHelp: '',
    }));
  };

  const handleModuleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newModuleId: null | string = null;
    modules.forEach((module) => {
      if (module.mac === e.target.value) {
        newModuleId = module.mac;
      }
    });
    setState((prevState) => ({
      ...prevState,
      module: newModuleId,
    }));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prevState) => ({
      ...prevState,
      stationName: e.target.value,
      nameValidationResult: null,
      nameValidationHelp: '',
    }));
  };

  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prevState) => ({
      ...prevState,
      radius: parseInt(e.target.value),
      radiusValidationResult: null,
      radiusValidationHelp: '',
    }));
  };

  const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const lat = parseFloat(e.target.value);
    if (isNaN(lat)) {
      setState((prevState) => ({
        ...prevState,
        lat: null,
        latValidationResult: null,
        latValidationHelp: '',
      }));
      removeMarkers();
      return;
    }
    if (lat > 90) {
      setState((prevState) => ({
        ...prevState,
        latValidationResult: 'error',
        latValidationHelp: 'Latitude cannot be greater than 90',
      }));
      removeMarkers();
    } else if (lat < -90) {
      setState((prevState) => ({
        ...prevState,
        latValidationResult: 'error',
        latValidationHelp: 'Latitude cannot be less than -90',
      }));
      removeMarkers();
    } else {
      setState((prevState) => ({
        ...prevState,
        latValidationResult: null,
        latValidationHelp: '',
      }));
      if (state.lng && !isNaN(state.lng)) {
        const map = getMapRef();
        removeMarkers();
        // @ts-ignore
        setMarker(window.L.marker([lat, state.lng]).addTo(map));
        map.setView([lat, state.lng], 12);
      }
    }
    setState((prevState) => ({
      ...prevState,
      lat,
    }));
  };

  const removeMarkers = () => {
    if (marker !== null) {
      const map = getMapRef();
      map.removeLayer(marker);
      setMarker(null);
    }
  };

  const handleLongChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const long = parseFloat(e.target.value);
    if (isNaN(long)) {
      setState((prevState) => ({
        ...prevState,
        lng: null,
        longValidationResult: null,
        longValidationHelp: '',
      }));
      removeMarkers();
      return;
    }
    if (long > 180) {
      setState((prevState) => ({
        ...prevState,
        longValidationResult: 'error',
        longValidationHelp: 'Longitude cannot be greater than 180',
      }));
      removeMarkers();
    } else if (long < -180) {
      setState((prevState) => ({
        ...prevState,
        longValidationResult: 'error',
        longValidationHelp: 'Longitude cannot be less than -180',
      }));
      removeMarkers();
    } else {
      if (state.lat && !isNaN(state.lat)) {
        const map = getMapRef();
        removeMarkers();
        // @ts-ignore
        setMarker(window.L.marker([state.lat, long]).addTo(map));
        map.setView([state.lat, long], 8);
      }
      setState((prevState) => ({
        ...prevState,
        longValidationResult: null,
        longValidationHelp: '',
      }));
    }
    setState((prevState) => ({
      ...prevState,
      lng: long,
    }));
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prevState) => ({
      ...prevState,
      state: parseInt(e.target.value),
    }));
  };

  const handleCapacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (state.capacity?.toString().length === 1 && e.target.value === '') {
      setState((prevState) => ({
        ...prevState,
        capacity: null,
      }));
      return;
    }
    setState((prevState) => ({
      ...prevState,
      capacity: Math.floor(parseInt(e.target.value)),
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
    const name = e.target.name;
    if (value !== RACK && value !== WAREHOUSE && value !== DOCK && value !== STORE) {
      removeMarkers();
      setState((prevState) => ({
        ...prevState,
        [name]: value,
        zoneType: value,
        lng: null,
        lat: null,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        [name]: value,
        zoneCoordinates: [],
      }));
    }
  };

  const handleRadioConfigurationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    const name = e.target.name;
    if (value !== RACK && value !== WAREHOUSE && value !== DOCK && value !== STORE) {
      removeMarkers();
      setState((prevState) => ({
        ...prevState,
        [name]: value,
        configuration: value as StationConfiguration,
        lng: null,
        lat: null,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        [name]: value,
        configuration: value as StationConfiguration,
        zoneCoordinates: [],
      }));
    }
  };

  const click = () => {
    const {
      configuration,
      slowZoneSpeed,
      radius,
      preventEndingRentals,
      preventStartingRentals,
      selectedRates,
    } = state;
    const isZone = configuration === ZONE;
    const validSlowZoneSpeedValue = Number.isNaN(parseInt(slowZoneSpeed || '', 10))
      ? null
      : parseInt(slowZoneSpeed || '', 10);
    const canAddRadius = [RACK, WAREHOUSE].includes(configuration);
    const validRadiusValue = Number.isNaN(parseInt(String(radius), 10))
      ? null
      : parseInt(String(radius), 10);

    if (canBeSubmitted()) {
      // info that all system types need
      const newStation: INewStation = {
        name: state.stationName.trim(),
        lat: isZone ? null : Number(state.lat),
        lng: isZone ? null : Number(state.lng),
        state: 1, // this is NOT the React state. it's the state of the station e.g. 'active' or 'unassigned'
        type: currentModeInfo.details.number,
        reservations: state.reservations,
        maxReservations: state.maxReservations,
        configuration:
          currentModeInfo.currentMode === 'bikeshare' ||
          currentModeInfo.currentMode === 'scooter'
            ? state.configuration
            : null,
        polygon_coordinates: isZone ? state.zoneCoordinates : null,
        zone_type: isZone ? state.zoneType : null,
        slow_zone_speed: isZone ? validSlowZoneSpeedValue : null,
        radius: canAddRadius ? validRadiusValue : null,
        prevent_ending_rentals: preventEndingRentals,
        prevent_starting_rentals: preventStartingRentals,
        rates_id: selectedRates.length ? selectedRates : null,
      };
      if (state.capacity) newStation.capacity = state.capacity;
      if (currentModeInfo.currentMode === 'bikeRoom' && !system.new_hardware) {
        newStation.module = state.module;
        if (newStation.module == null) newStation.state = 0;
      }

      // Create station and redirect to Media tab for image management
      CustomAsyncToast({
        promise: () => createLocation(newStation, selectedSystem?.gid),
        successMessage: () => 'Station Created Successfully',
        errorMessage: 'Station Creation Failed',
        loadingMessage: 'Creating Station...',
      }).then((result) => {
        if (result && result.data && result.data.gid) {
          // Navigate to the Media tab where users can add icon, banner, and gallery images
          history.push(`/locations/${result.data.gid}/edit?tab=media`);
        }
      });
    }
  };

  const canBeSubmitted = () => {
    let canBeSubmitted = true;

    if (state.stationName === '') {
      setState((prevState) => ({
        ...prevState,
        nameValidationResult: 'error',
        nameValidationHelp: 'A Name is Required to Create',
      }));
      canBeSubmitted = false;
    }

    if (state.stationName) {
      state.stations.forEach((station) => {
        if (
          station.name.toLowerCase().replace(/\s/g, '') ===
            state.stationName.toLowerCase().replace(/\s/g, '') &&
          station.state === ACTIVE
        ) {
          canBeSubmitted = false;
          setState((prevState) => ({
            ...prevState,
            nameValidationResult: 'error',
            nameValidationHelp: 'The name you entered already exists',
          }));
        }
      });
    }

    if (state.stationName.length > 30) {
      setState((prevState) => ({
        ...prevState,
        nameValidationResult: 'error',
        nameValidationHelp: 'Station Name Must be Less then 30 Characters',
      }));
      canBeSubmitted = false;
    }

    if (state.configuration === ZONE) {
      // TODO: more specific
      if (outOfBounds(state.zoneCoordinates)) {
        displayError('The coordinates for your zone are invalid');
        canBeSubmitted = false;
      }

      if (state.zoneCoordinates.length <= 2) {
        displayError('A zone must be made of at least three points');
        canBeSubmitted = false;
      }

      const speedLimit = parseInt(state.slowZoneSpeed ?? '0', 10);

      if (Number.isNaN(speedLimit) || speedLimit < 0 || speedLimit > 999) {
        displayError(SLOW_ZONE_ERROR);
        setState((prevState) => ({
          ...prevState,
          slowZoneSpeedHelp: SLOW_ZONE_ERROR,
        }));
        canBeSubmitted = false;
      }
    } else {
      if (!state.lat) {
        setState((prevState) => ({
          ...prevState,
          latValidationResult: 'error',
          latValidationHelp: 'Latitude is required',
        }));
        canBeSubmitted = false;
      } else if (state.lat > 90) {
        setState((prevState) => ({
          ...prevState,
          latValidationResult: 'error',
          latValidationHelp: 'Latitude cannot be greater than 90',
        }));
        canBeSubmitted = false;
      } else if (state.lat < -90) {
        setState((prevState) => ({
          ...prevState,
          latValidationResult: 'error',
          latValidationHelp: 'Latitude cannot be less than -90',
        }));
        canBeSubmitted = false;
      }
      if (!state.lng) {
        setState((prevState) => ({
          ...prevState,
          longValidationResult: 'error',
          longValidationHelp: 'Longitude is required',
        }));
        canBeSubmitted = false;
      } else if (state.lng > 180) {
        setState((prevState) => ({
          ...prevState,
          longValidationResult: 'error',
          longValidationHelp: 'Longitude cannot be greater than 180',
        }));
        canBeSubmitted = false;
      } else if (state.lng < -180) {
        setState((prevState) => ({
          ...prevState,
          longValidationResult: 'error',
          longValidationHelp: 'Longitude cannot be less than -180',
        }));
        canBeSubmitted = false;
      }

      const radius = state.radius;

      if (
        [RACK, WAREHOUSE].includes(state.configuration) &&
        (Number.isNaN(radius) || radius < 1 || radius > RADIUS_UPPER_LIMIT)
      ) {
        setState((prevState) => ({
          ...prevState,
          radiusValidationResult: 'error',
          radiusValidationHelp: RADIUS_ERROR,
        }));
        canBeSubmitted = false;
      }
    }

    return canBeSubmitted;
  };

  const outOfBounds = (coords: number[][]): boolean => {
    // !! converts to boolean
    // find returns undefined if no match and casts to false (i.e., no out of bounds points)
    // or the pair of coordinates that match, which will be cast to true
    return !!coords.find(
      (latLng) => latLng[0] > 90 || latLng[0] < -90 || latLng[1] > 180 || latLng[1] < -180
    );
  };

  const content = () => {
    const { stationsTitle } = currentModeInfo.details;
    const { preventEndingRentals, preventStartingRentals, systemRates } = state;
    const moduleOptions = [
      ...modules.map((module) => {
        if (module.state === 0) {
          return {
            value: module.mac,
            name: module.mac,
            id: module.mac,
          };
        }
        return undefined;
      }),
      {
        value: 'null',
        name: 'None',
        id: 0,
      },
    ];

    return (
      <div>
        {Boolean(organizationInfo.id) && (
          <OrganizationSystemSelect
            organization_id={organizationInfo?.id}
            selectedSystem={{
              value: selectedSystem?.gid || '',
              name: selectedSystem?.name || '',
            }}
            description={'Select the system this station will be associated with'}
            onChange={(system: ISystem) => {
              setSelectedSystem(system || null);
            }}
          />
        )}

        <MovaticFieldGroupText
          id="name"
          name="name"
          label={`${stationsTitle} Name`}
          description="Visible to users to identify where they are renting from."
          value={state.stationName}
          hasError={state.nameValidationResult === 'error'}
          errorMessage={state.nameValidationHelp}
          onChange={handleNameChange}
        />

        {(currentModeInfo.currentMode === 'bikeshare' ||
          currentModeInfo.currentMode === 'scooter') && (
          <MovaticFieldGroupRadio
            label="Location Type"
            value={state?.configuration || 'rack'}
            onChange={handleRadioConfigurationChange}
            options={[
              {
                id: RACK,
                label: 'Rack-based',
                popoverContent: `Locations that have racks where ${currentModeInfo.details.pluralUnits.toLowerCase()} with smart locks can park`,
              },
              {
                id: ZONE,
                label: 'Zone-based',
                popoverContent: `Large areas where ${currentModeInfo.details.pluralUnits.toLowerCase()} can be parked`,
              },
              {
                id: DOCK,
                label: 'Dock-based',
                popoverContent: `Locations that have racks with smart locks for ${currentModeInfo.details.unitsTitle.toLowerCase()} parking`,
              },
              {
                id: STORE,
                label: 'Store',
                popoverContent: `Staffed location where users can rent or park ${currentModeInfo.details.pluralUnits.toLowerCase()}`,
              },
              {
                id: WAREHOUSE,
                label: 'Warehouse',
                popoverContent: `Locations where ${currentModeInfo.details.pluralUnits.toLowerCase()} are stored. They are not visible to users.`,
              },
            ]}
          />
        )}
        {(currentModeInfo.currentMode === 'bikeshare' ||
          currentModeInfo.currentMode === 'scooter') &&
          state.configuration === ZONE && (
            <MovaticFieldGroupRadio
              label="Zone Type"
              value={state.zoneType || 'parking'}
              onChange={handleRadioChange}
              options={[
                {
                  id: 'parking',
                  label: 'Parking',
                  popoverContent: `Area for users to park their ${currentModeInfo.details.pluralUnits.toLowerCase()} at the end of rentals`,
                },
                {
                  id: 'preferred',
                  label: 'Preferred',
                  popoverContent: `Preferred area for users to park their ${currentModeInfo.details.pluralUnits.toLowerCase()} at the end of rentals`,
                },
                {
                  id: 'system',
                  label: 'System',
                  popoverContent: `Large area to mark zone where users can leave ${currentModeInfo.details.pluralUnits.toLowerCase()} in free roam systems`,
                },
                {
                  id: 'restricted',
                  label: 'Restricted',
                  popoverContent: `Area where users are not allowed to park ${currentModeInfo.details.pluralUnits.toLowerCase()} at the end of rentals`,
                },
                {
                  id: 'slowzone',
                  label: 'Slow Zone',
                  popoverContent: `Area where users are not allowed to ride ${currentModeInfo.details.pluralUnits.toLowerCase()} above a speed limit`,
                },
                {
                  id: 'nogo',
                  label: 'No Go',
                  popoverContent: `Area where users are prohibited from riding`,
                },
                {
                  id: 'deployment',
                  label: 'Deployment',
                  popoverContent: `A zone used to balance ${currentModeInfo.details.unitsTitle.toLowerCase()} deployments. This zone is not visible to users.`,
                },
              ]}
            />
          )}

        {(currentModeInfo.currentMode === 'bikeshare' ||
          currentModeInfo.currentMode === 'scooter') &&
          state.configuration === ZONE && (
            <MovaticFieldGroupOutlinedInput
              label="Speed Limit"
              description="Speed limit for this zone."
              type="number"
              name="slowZoneOptions"
              id="slowZoneOptions"
              value={state.slowZoneSpeed ?? ''}
              inputProps={{ min: 0, step: 1 }}
              onChange={handleSlowZoneOptionsChange}
              hasError={state.slowZoneSpeedHelp === 'error'}
              errorMessage={state.slowZoneSpeedHelp}
              addOn={'km/h'}
            />
          )}

        {![ZONE].includes(state.configuration) && (
          <MovaticFieldGroupOutlinedInput
            label="Radius"
            description="Radius of the area for this location in meters."
            type="number"
            name="radius"
            id="radius"
            value={state.radius || ''}
            inputProps={{ min: 0 }}
            onChange={handleRadiusChange}
            addOn={'meters'}
          />
        )}
        {
          // capacity for rack and dock based
          (currentModeInfo.currentMode === 'bikeshare' ||
            currentModeInfo.currentMode === 'scooter') &&
          [RACK, DOCK, WAREHOUSE].includes(state.configuration) ? (
            <MovaticFieldGroupOutlinedInput
              label="Station Capacity"
              description="The number of spots available for return. Leave blank for unlimited capacity."
              type="number"
              name="capacity"
              id="capacity"
              value={state.capacity ?? ''}
              inputProps={{ min: 0, step: 1 }}
              onChange={handleCapacityChange}
            />
          ) : null
        }
        {
          // user does not choose capacity for bike room
          currentModeInfo.currentMode === 'bikeRoom' && !system.new_hardware ? (
            <div>
              <MovaticFieldGroupSelect
                label="Module"
                description="What module is assigned to the hardware"
                name={'module'}
                optionNodes={moduleOptions.map((option, index) => (
                  <MenuItem key={index} value={option?.value}>
                    {option?.name}
                  </MenuItem>
                ))}
                value={state.module ? state.module : 'None'}
                onChange={handleModuleChange}
                id="module"
              />
            </div>
          ) : null
        }

        {state.module == null || state.module === '' ? null : (
          <MovaticFieldGroupSelect
            label={`${stationsTitle} Status`}
            description={`A ${stationsTitle.toLowerCase()} must be active for rentals to occur at it.`}
            name={'module'}
            optionNodes={[
              { value: 1, name: 'Active' },
              { value: 3, name: 'Maintenance' },
            ].map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
            value={state.state === 2 ? '3' : state.state}
            onChange={handleStateChange}
            id="state"
          />
        )}
        {system.reservations ? (
          <div>
            <MovaticFieldGroupCheck
              label="Reservations"
              description="Allow users to make reservations ahead of time"
              id="reservations"
              name="reservations"
              onChange={onChangeValue}
              checked={state.reservations}
            />
            {state.reservations ? (
              <MovaticFieldGroupSelect
                label="Max Reservations"
                description="Limit the amount of reservations"
                name={'maxReservations'}
                optionNodes={[
                  { value: '0', label: 'none' },
                  { value: '1', label: '1' },
                  { value: '2', label: '2' },
                  { value: '3', label: '3' },
                  { value: '4', label: '4' },
                  { value: '5', label: '5' },
                  { value: '10', label: '10' },
                  { value: '20', label: '20' },
                ].map((option, index) => (
                  <MenuItem key={index} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
                value={state.maxReservations}
                onChange={onChangeValue}
                id="maxReservations"
              />
            ) : null}
          </div>
        ) : null}

        {[
          {
            id: 'preventEndingRentals',
            label: 'Prevent ending rentals',
            description:
              'Check this option if you do not want a user to be able to end a rental in this location.',
            checked: preventEndingRentals,
          },
          {
            id: 'preventStartingRentals',
            label: 'Prevent starting rentals',
            description:
              'Check this option if you do not want a user to be able to start a rental in this location.',
            checked: preventStartingRentals,
          },
        ].map(({ id, label, description, checked }) => (
          <MovaticFieldGroupCheck
            key={id}
            label={label}
            description={description}
            id={id}
            name={id}
            onChange={onChangeValue}
            checked={checked}
          />
        ))}
        {systemRates.length > 0 && system.new_hardware && (
          <MovaticFieldGroupMultiSelect
            label="Assigned Rates"
            description="Select optional rates for this location."
            value={state.componentRates}
            onChange={(event: React.SyntheticEvent, newValue: any[]) => {
              setState((state) => ({
                ...state,
                selectedRates: newValue.map((rate) => rate.value),
                componentRates: newValue,
              }));
            }}
            options={systemRates}
          />
        )}

        <div className="fieldTopPadding">
          {state.configuration !== ZONE ? (
            <div>
              <MovaticFieldGroupOutlinedInput
                type={'number'}
                description={`The latitude of the ${stationsTitle.toLowerCase()}. (Enter the value or click the location on the map)`}
                label={`${stationsTitle} Latitude`}
                value={state.lat !== null ? state.lat : ''}
                allowNegativeNumbers
                hasError={state.latValidationResult === 'error'}
                errorMessage={state.latValidationHelp}
                onChange={handleLatChange}
                id="lat"
              />

              <MovaticFieldGroupOutlinedInput
                type={'number'}
                description={`The longitude of the ${stationsTitle.toLowerCase()}. (Enter the value or click the location on the map)`}
                label={`${stationsTitle} Longitude`}
                value={state.lng !== null ? state.lng : ''}
                allowNegativeNumbers
                hasError={state.longValidationResult === 'error'}
                errorMessage={state.longValidationHelp}
                onChange={handleLongChange}
                id="lng"
              />
            </div>
          ) : (
            <FormItem
              label="Edit Zone"
              content="Click the pentagon to begin drawing the zone. Click the edit icon to edit the shape afterward, or the delete icon to remove it and start over."
            />
          )}
        </div>
        <Stack pt={2}>
          <Map
            center={[0, 0]}
            zoom={2}
            ref={mapRef}
            maxBounds={[
              [90, 180],
              [-90, -180],
            ]}
            onClick={state.configuration !== ZONE ? handleMapClick : null}
          >
            {state.configuration === ZONE && (
              <FeatureGroup>
                <EditControl
                  onCreated={onCreatePolygon}
                  onDeleted={onDeletePolygon}
                  onEdited={onEditPolygon}
                  draw={{
                    polygon:
                      state.zoneCoordinates.length === 0
                        ? {
                            allowIntersection: false,
                            shapeOptions: {
                              color: MOVATIC_PRIMARY_COLOR(1),
                            },
                          }
                        : false,
                    rectangle: false,
                    circle: false,
                    polyline: false,
                    marker: false,
                    circlemarker: false,
                  }}
                />
              </FeatureGroup>
            )}
            <GenericMapTileLayer />
          </Map>
        </Stack>
      </div>
    );
  };

  const isLoading = !systemLoaded || ratesLoading;

  return (
    <PageContainer
      isLoading={organizationInfo?.id ? !Boolean(organizationInfo?.id) : isLoading}
    >
      <CardView
        content={
          <>
            <Tabs value="details" textColor="inherit" sx={{ px: 2 }}>
              <Tab label="Details" value="details" />
              <Tab label="Media" value="media" disabled />
            </Tabs>
            <Stack px={2} pb={2}>
              {content()}
            </Stack>
          </>
        }
        title={`Add a ${currentModeInfo.details.stationsTitle || 'Location'}`}
        headerActions={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => history.push('/locations'),
              },
              {
                label: 'Save',
                onClick: click,
                dataId: 'save-button',
                disabled:
                  (!findAccess(systemAccess).includes('physical') && !movaticAccess) ||
                  state.isLoading,
                startIcon: <SaveIcon />,
              },
            ]}
          />
        }
      />
    </PageContainer>
  );
};

export default connect(
  (state: {
    admin: {
      systemAccess: number;
      admin: {
        admin: {
          movaticAccess: boolean;
          partner: number;
        };
      };
    };
    system: {
      current: ISystem;
      isLoaded: boolean;
    };
    mode: IMode;
    module: {
      modules: { mac: string; state: number }[];
    };
  }) => ({
    systemAccess: state.admin.systemAccess,
    system: state.system.current,
    modeInfo: state.mode,
    modules: state.module.modules,
    systemLoaded: state.system.isLoaded,
    movaticAccess:
      state.admin.admin.admin.movaticAccess && !state.admin.admin.admin.partner,
  }),
  () => ({})
)(StationAdd);
